import React, { useContext, useMemo } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import { useParams } from 'react-router'
import { capitalise } from 'lib/string/stringUtils'
import { HOME_PAGE_OFFER_TYPES, SupportedDestinations } from 'home/constants'
import Pane from 'components/Common/Pane'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import GeoContext from 'contexts/geoContext'
import { getHomeOfferListParams } from 'home/helpers'
import { usePlaceByName } from 'hooks/usePlace'
import { MAX_CAROUSEL_ITEM_COUNT } from './common/constants'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { themeClassName } from 'lib/theme/themeUtils'
import HeadlineTrustIndicator from './MobileHeadlineTest/HeadlineTrustIndicator'
import LineTagAltIcon from 'components/Luxkit/Icons/line/LineTagAltIcon'
import LineGlassMartiniAltIcon from 'components/Luxkit/Icons/line/LineGlassMartiniAltIcon'
import LineGlobeAltIcon from 'components/Luxkit/Icons/line/LineGlobeAltIcon'
import LinePhoneAltIcon from 'components/Luxkit/Icons/line/LinePhoneAltIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import { SvgIconProps } from 'components/Luxkit/SvgIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

interface Props {
  className?: string;
  showMarketingHeadline?: boolean
}

const iconBaseProps: SvgIconProps = {
  colour: 'highlight-primary-normal',
}

const content = [
  { Icon: <LineTagAltIcon {...iconBaseProps} />, text: 'Save up to 70% on exclusive travel offers' },
  { Icon: <LineGlassMartiniAltIcon {...iconBaseProps} />, text: 'Extra inclusions like drinks, dining, transfers & more' },
  { Icon: <LineGlobeAltIcon {...iconBaseProps} />, text: 'Thousands of hotels, tours, cruises, flights & experiences' },
  { Icon: <LinePhoneAltIcon {...iconBaseProps} />, text: '24/7 customer service by phone or email', excludedRegion: 'IN' },
]

function HeroOfferCarouselSection(props: Props) {
  const { className, showMarketingHeadline } = props
  const { destination } = useParams<{ destination?: SupportedDestinations}>()
  const { currentRegionCode, geoStateName } = useContext(GeoContext)
  const destinationId = usePlaceByName(destination)?.id ?? ANYWHERE_PLACE_ID

  const filters = useMemo(() => {
    if (destination) {
      return {
        offerTypes: HOME_PAGE_OFFER_TYPES,
        limit: MAX_CAROUSEL_ITEM_COUNT,
        placeIds: [destinationId],
      }
    } else {
      return getHomeOfferListParams({
        region: currentRegionCode,
        subregion: geoStateName,
      })
    }
  }, [currentRegionCode, destination, destinationId, geoStateName])

  const filteredContent = useMemo(() => content.filter(item => currentRegionCode !== item.excludedRegion), [currentRegionCode])

  const regularHeadline = <>
    {!destination && <Heading variant="heading2">
      Today's top <i>exclusive</i> offers
    </Heading>}
    {destination && (
      <Heading variant="heading2">
        Today's top exclusive <i>{capitalise(destination)}</i> offers
      </Heading>
    )}
  </>

  return (
    <Pane paletteType="inverse" className={className}>
      <Group direction="vertical" gap={24}>
        <LayoutContainer>
          {!!showMarketingHeadline && <>
            <CSSBreakpoint max="mobile">
              <Group direction="vertical" gap={16}>
                <Heading variant="heading1">
                  <i>More</i> with every booking
                </Heading>
                <Group direction="vertical" gap={8}>
                  {filteredContent.map(({ Icon, text }) => <BodyTextBlock
                      key={text}
                      variant="small"
                      colour="neutral-one"
                      startIcon={Icon}
                    >
                    {text}
                  </BodyTextBlock>)}
                </Group>
                <HeadlineTrustIndicator />
              </Group>
            </CSSBreakpoint>
            <CSSBreakpoint min="tablet">
              regularHeadline
            </CSSBreakpoint>
          </>
          }
          {!showMarketingHeadline && regularHeadline}
        </LayoutContainer>
        <OfferListCarousel
          filters={filters}
          className={themeClassName('inverse')}
        />
      </Group>
    </Pane>
  )
}

export default React.memo(HeroOfferCarouselSection)
